import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditCampaignQuoteTemplateItemModal from '@modals/CreateOrEditCampaignQuoteTemplateItemModal'
import CreateOrEditCampaignQuoteTemplateModal from '@modals/CreateOrEditCampaignQuoteTemplateModal'
import CreateOrEditCampaignQuoteTemplateSectionModal from '@modals/CreateOrEditCampaignQuoteTemplateSectionModal'
import DownloadQuoteTemplateModal from '@modals/DownloadQuoteTemplateModal'
import EditCampaignQuoteTemplateSectionSortModal from '@modals/EditCampaignQuoteTemplateSectionSortModal'
import EditItemOptionsModal from '@modals/EditItemOptionsModal'
import NewOrderModal from '@modals/NewOrderModal'
import PriceAutoUpdatedModal from '@modals/PriceAutoUpdatedModal/PriceAutoUpdateModal'

import type { CampaignQuoteTemplateItemModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditCampaignQuoteTemplateItemModal: false,
  showCreateOrEditCampaignQuoteTemplateModal: false,
  showCreateOrEditCampaignQuoteTemplateSectionModal: false,
  showDownloadQuoteTemplateModal: false,
  showEditCampaignQuoteTemplateSectionSortModal: false,
  showEditItemOptionsModal: false,
  showNewOrderModal: false,
  showPriceAutoUpdatedModal: false,
  priceAutoUpdatedModalPayload: undefined as { campaignQuoteTemplateItem: CampaignQuoteTemplateItemModel } | undefined,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditCampaignQuoteTemplateItemModal: {
      closeModal: () => setState({ showCreateOrEditCampaignQuoteTemplateItemModal: false }),
      createCampaignQuoteTemplateItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      duplicateCampaignQuoteTemplateItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateCampaignQuoteTemplateItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditCampaignQuoteTemplateModal: {
      closeModal: () => setState({ showCreateOrEditCampaignQuoteTemplateModal: false }),
      updateCampaignQuoteTemplate: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditCampaignQuoteTemplateSectionModal: {
      closeModal: () => setState({ showCreateOrEditCampaignQuoteTemplateSectionModal: false }),
      updateCampaignQuoteTemplateSection: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    DownloadQuoteTemplateModal: {
      closeModal: () => setState({ showDownloadQuoteTemplateModal: false }),
    },
    EditCampaignQuoteTemplateSectionSortModal: {
      closeModal: () => setState({ showEditCampaignQuoteTemplateSectionSortModal: false }),
      updateCampaignQuoteTemplateSectionSort: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    EditItemOptionsModal: {
      closeModal: () => setState({ showEditItemOptionsModal: false }),
      updateItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    NewOrderModal: {
      addItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showNewOrderModal: false }),
    },
    PriceAutoUpdatedModal: {
      closeModal: () => setState({ showPriceAutoUpdatedModal: false, priceAutoUpdatedModalPayload: undefined }),
      updateCampaignQuoteTemplateItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

type EditorProps = {
  agencyId: number,
}

const Editor = (props: EditorProps) => {
  const { agencyId } = props

  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditCampaignQuoteTemplateItemModal,
    showCreateOrEditCampaignQuoteTemplateModal,
    showCreateOrEditCampaignQuoteTemplateSectionModal,
    showDownloadQuoteTemplateModal,
    showEditCampaignQuoteTemplateSectionSortModal,
    showEditItemOptionsModal,
    showNewOrderModal,
    showPriceAutoUpdatedModal,
    priceAutoUpdatedModalPayload,
  } = state

  const { campaignQuoteTemplateId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData }, topModalKey,
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showCreateOrEditCampaignQuoteTemplateModal: (payload: object) => {
        setModalData('CreateOrEditCampaignQuoteTemplateModal', payload)
        setState({ showCreateOrEditCampaignQuoteTemplateModal: true })
      },
      showCreateOrEditCampaignQuoteTemplateItemModal: (payload: object) => {
        setModalData('CreateOrEditCampaignQuoteTemplateItemModal', payload)
        setState({ showCreateOrEditCampaignQuoteTemplateItemModal: true })
      },
      showCreateOrEditCampaignQuoteTemplateSectionModal: (payload: object) => {
        setModalData('CreateOrEditCampaignQuoteTemplateSectionModal', payload)
        setState({ showCreateOrEditCampaignQuoteTemplateSectionModal: true })
      },
      showDownloadQuoteTemplateModal: (payload: object) => {
        setModalData('DownloadQuoteTemplateModal', payload)
        setState({ showDownloadQuoteTemplateModal: true })
      },
      showEditCampaignQuoteTemplateSectionSortModal: (payload: object) => {
        setModalData('EditCampaignQuoteTemplateSectionSortModal', payload)
        setState({ showEditCampaignQuoteTemplateSectionSortModal: true })
      },
      showEditItemOptionsModal: (payload: { campaignQuoteTemplateItem: CampaignQuoteTemplateItemModel }) => {
        setModalData('EditItemOptionsModal', payload)
        setState({ showEditItemOptionsModal: true })
      },
      showNewOrderModal: (payload: object) => {
        setModalData('NewOrderModal', payload)
        setState({ showNewOrderModal: true })
      },
      showPriceAutoUpdatedModal: (payload: { campaignQuoteTemplateItem: CampaignQuoteTemplateItemModel }) => {
        setState({ showPriceAutoUpdatedModal: true, priceAutoUpdatedModalPayload: payload })
      },
      updateCampaignQuoteTemplate: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [topModalKey])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent agencyId={agencyId} campaignQuoteTemplateId={campaignQuoteTemplateId} />

        <CreateOrEditCampaignQuoteTemplateItemModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditCampaignQuoteTemplateItemModal', setState)}
          showModal={showCreateOrEditCampaignQuoteTemplateItemModal}
        />

        <CreateOrEditCampaignQuoteTemplateModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditCampaignQuoteTemplateModal', setState)}
          showModal={showCreateOrEditCampaignQuoteTemplateModal}
        />

        <CreateOrEditCampaignQuoteTemplateSectionModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditCampaignQuoteTemplateSectionModal', setState)}
          showModal={showCreateOrEditCampaignQuoteTemplateSectionModal}
        />

        <DownloadQuoteTemplateModal
          callbacks={callbacks('DownloadQuoteTemplateModal', setState)}
          showModal={showDownloadQuoteTemplateModal}
        />

        <EditCampaignQuoteTemplateSectionSortModal
          // @ts-expect-error TS2322
          callbacks={callbacks('EditCampaignQuoteTemplateSectionSortModal', setState)}
          showModal={showEditCampaignQuoteTemplateSectionSortModal}
        />

        <EditItemOptionsModal
          // @ts-expect-error TS2322
          callbacks={callbacks('EditItemOptionsModal', setState)}
          requestOptions={{

          }}
          showModal={showEditItemOptionsModal}
        />

        <NewOrderModal
          callbacks={callbacks('NewOrderModal', setState)}
          showModal={showNewOrderModal}
        />

        <PriceAutoUpdatedModal
          campaignQuoteTemplateItem={priceAutoUpdatedModalPayload?.campaignQuoteTemplateItem}
          // @ts-expect-error TS2322
          callbacks={callbacks('PriceAutoUpdatedModal', setState)}
          showModal={showPriceAutoUpdatedModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Editor
